// Store event handlers for tooltips to allow removal when needed
const tooltipEvents = {
  openButtonHandlers: new Map<Element, EventListener>(),
  closeButtonHandlers: new Map<Element, EventListener>(),
  documentClickHandler: null as EventListener | null,
  initialized: false,

  // Add click events to tooltip open buttons
  addOpenEvents() {
    const tooltipOpenButtons = document.querySelectorAll('.js-tooltip-open-button');

    tooltipOpenButtons.forEach((button) => {
      // Skip if handler already exists for this button
      if (this.openButtonHandlers.has(button)) return;

      const handler = (event: Event) => {
        event.stopPropagation(); // Prevent document click from immediately closing
        const tooltipContainer = button.querySelector('.js-tooltip-container');
        if (tooltipContainer) {
          // Close other open tooltips
          document.querySelectorAll('.js-tooltip-container.is-active').forEach((openTooltip) => {
            if (openTooltip !== tooltipContainer) {
              openTooltip.classList.remove('is-active');
            }
          });
          tooltipContainer.classList.add('is-active');
        }
      };

      try {
        button.addEventListener('click', handler);
        this.openButtonHandlers.set(button, handler as EventListener);
      } catch (error) {
        console.error('Error adding tooltip open event:', error);
      }
    });
  },

  // Add click events to tooltip close triggers
  addCloseEvents() {
    const tooltipCloseTriggers = document.querySelectorAll('.js-tooltip-close-trigger');

    tooltipCloseTriggers.forEach((trigger) => {
      // Skip if handler already exists for this trigger
      if (this.closeButtonHandlers.has(trigger)) return;

      const handler = (event: Event) => {
        event.stopPropagation();
        const tooltipContainer = trigger.closest('.js-tooltip-container');
        if (tooltipContainer) {
          tooltipContainer.classList.remove('is-active');
        }
      };

      try {
        trigger.addEventListener('click', handler);
        this.closeButtonHandlers.set(trigger, handler as EventListener);
      } catch (error) {
        console.error('Error adding tooltip close event:', error);
      }
    });
  },

  // Add document click event to close tooltips when clicking outside
  addDocumentClickEvent() {
    // Remove existing handler if any
    this.removeDocumentClickEvent();

    const handler = (event: MouseEvent) => {
      const openTooltips = document.querySelectorAll('.js-tooltip-container.is-active');
      openTooltips.forEach((tooltip) => {
        const openButton = tooltip.parentElement;
        const target = event.target as Element;
        if (openButton && !openButton.contains(target) && !tooltip.contains(target)) {
          tooltip.classList.remove('is-active');
        }
      });
    };

    try {
      document.addEventListener('click', handler);
      this.documentClickHandler = handler as EventListener;
    } catch (error) {
      console.error('Error adding document click event:', error);
    }
  },

  // Remove all tooltip event listeners
  removeAllEvents() {
    this.removeOpenEvents();
    this.removeCloseEvents();
    this.removeDocumentClickEvent();
    this.initialized = false;
  },

  // Remove open button event listeners
  removeOpenEvents() {
    this.openButtonHandlers.forEach((handler, button) => {
      if (button) {
        try {
          button.removeEventListener('click', handler);
        } catch (error) {
          console.error('Error removing tooltip open event:', error);
        }
      }
    });
    this.openButtonHandlers.clear();
  },

  // Remove close trigger event listeners
  removeCloseEvents() {
    this.closeButtonHandlers.forEach((handler, trigger) => {
      if (trigger) {
        try {
          trigger.removeEventListener('click', handler);
        } catch (error) {
          console.error('Error removing tooltip close event:', error);
        }
      }
    });
    this.closeButtonHandlers.clear();
  },

  // Remove document click event listener
  removeDocumentClickEvent() {
    if (this.documentClickHandler) {
      try {
        document.removeEventListener('click', this.documentClickHandler);
        this.documentClickHandler = null;
      } catch (error) {
        console.error('Error removing document click event:', error);
      }
    }
  },
};

// Store event handlers for entry management to allow removal when needed
const entryEvents = {
  addButtonHandlers: new Map<Element, EventListener>(),
  deleteButtonHandlers: new Map<Element, EventListener>(),
  allCheckboxHandlers: new Map<Element, EventListener>(),
  otherCheckboxHandlers: new Map<Element, EventListener>(),
  initialized: false,

  // Add click events to add entry buttons
  addAddButtonEvents() {
    const addButtons = document.querySelectorAll('.js-add-entry');

    addButtons.forEach((button) => {
      // Skip if handler already exists for this button
      if (this.addButtonHandlers.has(button)) return;

      const entryList = button.closest('.js-form-block')?.querySelector('.js-entry-list');
      if (!entryList) return;

      const handler = this.createAddButtonHandler(button, entryList);

      try {
        button.addEventListener('click', handler);
        this.addButtonHandlers.set(button, handler);
      } catch (error) {
        console.error('Error adding entry add button event:', error);
      }
    });
  },

  // Create handler for add button
  createAddButtonHandler(button: Element, entryList: Element): EventListener {
    return () => {
      const formBlock = button.closest('.js-form-block');
      if (!formBlock) return;

      const firstEntry = entryList.children[0];
      if (!firstEntry) return;

      const areaIndex = Array.from(document.querySelectorAll('.js-form-block')).indexOf(formBlock);
      const timestamp = Date.now();
      const uniqueBase = `${areaIndex}-${timestamp}`;

      const newEntry = this.createNewEntry(firstEntry, areaIndex, uniqueBase);
      entryList.appendChild(newEntry);

      // Add delete event to the new entry
      this.addDeleteButtonEvent(newEntry);

      // Update delete buttons state
      this.updateDeleteButtonsState(entryList);
    };
  },

  // Create a new entry from template
  createNewEntry(template: Element, areaIndex: number, uniqueBase: string): Element {
    const newEntry = template.cloneNode(true) as Element;

    // Update IDs, names, and other attributes to make them unique
    const elements = newEntry.querySelectorAll('[id], [name], label[for], [data-modal-target], [data-tab]');
    elements.forEach((el) => {
      if (el.hasAttribute('id')) {
        const oldId = el.getAttribute('id');
        if (oldId) {
          const newId = `${oldId}-${uniqueBase}`;
          el.setAttribute('id', newId);
          const label = newEntry.querySelector(`label[for="${oldId}"]`);
          if (label) {
            label.setAttribute('for', newId);
          }
        }
      }
      if (el.hasAttribute('name')) {
        const oldName = el.getAttribute('name');
        if (oldName) {
          el.setAttribute('name', `${oldName}-${uniqueBase}`);
        }
      }
      if (el.hasAttribute('data-tab')) {
        const oldTab = el.getAttribute('data-tab');
        if (oldTab) {
          el.setAttribute('data-tab', `${oldTab}-${uniqueBase}`);
        }
      }
      if (el.hasAttribute('data-modal-target')) {
        const oldTarget = el.getAttribute('data-modal-target');
        if (!el.hasAttribute('data-unique-id')) {
          if (oldTarget) {
            el.setAttribute('data-modal-target', `${oldTarget}-${uniqueBase}`);
          }
        }
      }
      if (el.classList.contains('--has-value')) {
        el.classList.remove('--has-value');
      }
    });

    // Reset form element values
    newEntry.querySelectorAll('input, select, textarea').forEach((el) => {
      if (el instanceof HTMLInputElement) {
        if (el.type === 'checkbox' || el.type === 'radio') {
          el.checked = el.defaultChecked;
        } else {
          el.value = el.defaultValue;
        }
      } else if (el instanceof HTMLSelectElement) {
        el.selectedIndex = 0;
      } else if (el instanceof HTMLTextAreaElement) {
        el.value = '';
      }
    });

    return newEntry;
  },

  // Add click events to delete entry buttons
  addDeleteButtonEvents() {
    document.querySelectorAll('.js-entry-list > li').forEach((entry) => {
      this.addDeleteButtonEvent(entry);
    });
  },

  // Add delete event to a specific entry
  addDeleteButtonEvent(entry: Element) {
    const deleteButton = entry.querySelector('.js-delete-entry');
    if (!deleteButton || this.deleteButtonHandlers.has(deleteButton)) return;

    const handler = (event: Event) => {
      event.preventDefault();
      const entryList = entry.parentElement;
      if (entryList) {
        entry.remove();
        this.updateDeleteButtonsState(entryList);
      }
    };

    try {
      deleteButton.addEventListener('click', handler);
      this.deleteButtonHandlers.set(deleteButton, handler as EventListener);
    } catch (error) {
      console.error('Error adding entry delete button event:', error);
    }
  },

  // Update the state of delete buttons based on entry count
  updateDeleteButtonsState(entryList: Element) {
    const entries = Array.from(entryList.children).filter((child) => child.tagName === 'LI');

    const formBlock = entryList.closest('.js-form-block');
    if (!formBlock) return;

    const addButton = formBlock.querySelector('.js-add-entry');
    const allCheckbox = formBlock.querySelector('.js-all') as HTMLInputElement;
    const maxEntries = parseInt(entryList.getAttribute('data-entries') || '3', 10);

    // Update delete buttons
    const canDelete = entries.length > 1;
    entries.forEach((li) => {
      const deleteButton = li.querySelector('.js-delete-entry') as HTMLButtonElement;
      if (deleteButton) {
        deleteButton.disabled = !canDelete;
      }
    });

    // Update add button
    if (addButton) {
      (addButton as HTMLButtonElement).disabled = entries.length >= maxEntries || (allCheckbox?.checked ?? false);
    }
  },

  // Add change events to "all" checkboxes
  addAllCheckboxEvents() {
    document.querySelectorAll('.js-form-block').forEach((formBlock) => {
      const allCheckbox = formBlock.querySelector('.js-all') as HTMLInputElement;
      if (!allCheckbox || this.allCheckboxHandlers.has(allCheckbox)) return;

      const entryList = formBlock.querySelector('.js-entry-list');
      if (!entryList) return;

      const handler = () => {
        const isChecked = allCheckbox.checked;
        const addButton = formBlock.querySelector('.js-add-entry') as HTMLButtonElement;

        if (addButton) {
          const entries = Array.from(entryList.children).filter((child) => child.tagName === 'LI');
          const maxEntries = parseInt(entryList.getAttribute('data-entries') || '3', 10);
          addButton.disabled = isChecked || entries.length >= maxEntries;
        }

        entryList.classList.toggle('-is-disabled', isChecked);
      };

      try {
        allCheckbox.addEventListener('change', handler);
        this.allCheckboxHandlers.set(allCheckbox, handler as EventListener);
      } catch (error) {
        console.error('Error adding all checkbox event:', error);
      }
    });
  },

  // Add change events to "other" checkboxes
  addOtherCheckboxEvents() {
    const formPurposeOtherCheckbox = document.getElementById('form-purpose-other') as HTMLInputElement;
    if (!formPurposeOtherCheckbox || this.otherCheckboxHandlers.has(formPurposeOtherCheckbox)) return;

    const formPurposeOtherList = document.getElementById('form-purpose-other-list') as HTMLUListElement;
    if (!formPurposeOtherList) return;

    const handler = () => {
      const isChecked = formPurposeOtherCheckbox.checked;
      formPurposeOtherList.classList.toggle('-is-disabled', !isChecked);
      formPurposeOtherList.querySelectorAll('input, select, textarea').forEach((inputElement) => {
        (inputElement as HTMLInputElement).disabled = !isChecked;
      });
    };

    try {
      formPurposeOtherCheckbox.addEventListener('change', handler);
      this.otherCheckboxHandlers.set(formPurposeOtherCheckbox, handler as EventListener);

      // Initialize state
      handler();
    } catch (error) {
      console.error('Error adding other checkbox event:', error);
    }
  },

  // Remove all entry management event listeners
  removeAllEvents() {
    this.removeAddButtonEvents();
    this.removeDeleteButtonEvents();
    this.removeAllCheckboxEvents();
    this.removeOtherCheckboxEvents();
    this.initialized = false;
  },

  // Remove add button event listeners
  removeAddButtonEvents() {
    this.addButtonHandlers.forEach((handler, button) => {
      if (button) {
        try {
          button.removeEventListener('click', handler);
        } catch (error) {
          console.error('Error removing add button event:', error);
        }
      }
    });
    this.addButtonHandlers.clear();
  },

  // Remove delete button event listeners
  removeDeleteButtonEvents() {
    this.deleteButtonHandlers.forEach((handler, button) => {
      if (button) {
        try {
          button.removeEventListener('click', handler);
        } catch (error) {
          console.error('Error removing delete button event:', error);
        }
      }
    });
    this.deleteButtonHandlers.clear();
  },

  // Remove all checkbox event listeners
  removeAllCheckboxEvents() {
    this.allCheckboxHandlers.forEach((handler, checkbox) => {
      if (checkbox) {
        try {
          checkbox.removeEventListener('change', handler);
        } catch (error) {
          console.error('Error removing all checkbox event:', error);
        }
      }
    });
    this.allCheckboxHandlers.clear();
  },

  // Remove other checkbox event listeners
  removeOtherCheckboxEvents() {
    this.otherCheckboxHandlers.forEach((handler, checkbox) => {
      if (checkbox) {
        try {
          checkbox.removeEventListener('change', handler);
        } catch (error) {
          console.error('Error removing other checkbox event:', error);
        }
      }
    });
    this.otherCheckboxHandlers.clear();
  },
};

// Main function to initialize entry management
function addRemoveEntry() {
  // Initialize all entry events if not already initialized
  if (!entryEvents.initialized) {
    entryEvents.addAddButtonEvents();
    entryEvents.addDeleteButtonEvents();
    entryEvents.addAllCheckboxEvents();
    entryEvents.addOtherCheckboxEvents();

    // Update initial state of all entry lists
    document.querySelectorAll('.js-entry-list').forEach((entryList) => {
      entryEvents.updateDeleteButtonsState(entryList);
    });

    entryEvents.initialized = true;
  } else {
    // Just update events for any new elements
    entryEvents.addAddButtonEvents();
    entryEvents.addDeleteButtonEvents();
    entryEvents.addAllCheckboxEvents();
    entryEvents.addOtherCheckboxEvents();
  }
}

export const domHelperUtils = {
  scrollToTop: () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, 
  disableBodyScroll() {
    document.body.style.overflow = 'hidden';
  },
  enableBodyScroll() {
    document.body.style.overflow = '';
  },
  tooltip:{
    // Initialize all tooltip events
    initializeTooltips() {
      tooltipEvents.addOpenEvents();
      tooltipEvents.addCloseEvents();
      tooltipEvents.addDocumentClickEvent();
      tooltipEvents.initialized = true;
    },

    // Update tooltips when DOM changes
    updateTooltips() {
      if (tooltipEvents.initialized) {
        tooltipEvents.addOpenEvents();
        tooltipEvents.addCloseEvents();
      } else {
        this.initializeTooltips();
      }
    },

    // Check if tooltips are initialized
    areTooltipsInitialized() {
      return tooltipEvents.initialized;
    },

    // Remove all tooltip events
    removeTooltipEvents() {
      tooltipEvents.removeAllEvents();
    },

    // Close all open tooltips
    closeAllTooltips() {
      document.querySelectorAll('.js-tooltip-container.is-active').forEach((tooltip) => {
        tooltip.classList.remove('is-active');
      });
    },
  },
  entryManager: {
    // Initialize all entry management events
    initializeEntryManagement() {
      addRemoveEntry();
    },

    // Update entry management when DOM changes
    updateEntryManagement() {
      if (entryEvents.initialized) {
        entryEvents.addAddButtonEvents();
        entryEvents.addDeleteButtonEvents();
        entryEvents.addAllCheckboxEvents();
        entryEvents.addOtherCheckboxEvents();
      } else {
        this.initializeEntryManagement();
      }
    },

    // Check if entry management is initialized
    isEntryManagementInitialized() {
      return entryEvents.initialized;
    },

    // Remove all entry management events
    removeEntryManagementEvents() {
      entryEvents.removeAllEvents();
    },
  },
  toggle: {
    initOpen(el: HTMLElement) {
      el.style.display = 'block';
      el.style.height = 'auto';
    },
    openAccordion(el: HTMLElement) {
      el.style.display = 'block';
      const height = el.scrollHeight;
      el.style.height = '0px';
      el.style.overflow = 'hidden';
      requestAnimationFrame(() => {
        el.style.transition = 'height 0.3s ease';
        el.style.height = height + 'px';
        const cleanup = () => {
          el.style.height = 'auto';
          el.style.overflow = '';
          el.style.transition = '';
          el.removeEventListener('transitionend', cleanup);
        };
        el.addEventListener('transitionend', cleanup);
      });
    },
    closeAccordion(el: HTMLElement) {
      const height = el.scrollHeight;
      el.style.height = height + 'px';
      el.style.overflow = 'hidden';
      requestAnimationFrame(() => {
        el.style.transition = 'height 0.3s ease';
        el.style.height = '0px';
        const cleanup = () => {
          el.style.display = 'none';
          el.style.height = '';
          el.style.overflow = '';
          el.style.transition = '';
          el.removeEventListener('transitionend', cleanup);
        };
        el.addEventListener('transitionend', cleanup);
      });
    }
  }
};
